import { useEffect, useMemo } from 'react';
import { NavLink, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { useI18n } from '../../contexts/I18nContext';
import { ROLES } from '../../utils/constants';
import { TierBadge } from '../TierGate';

/* ─── Phase badge color map ─────────────────────────────────────── */
const PHASE_BADGE = {
  construction: { label: 'Construction', bg: 'bg-primary',  text: 'text-white' },
  handover:     { label: 'Handover',     bg: 'bg-warning',  text: 'text-dark'  },
  community:    { label: 'Community',    bg: 'bg-success',  text: 'text-white' },
};

/* ─── Portfolio / Admin navigation (outside a project) ──────────── */
const getPortfolioNav = (t) => [
  {
    section: t('sec_main') || 'Main',
    items: [
      { to: '/app/dashboard',      icon: 'bi-speedometer2',         label: 'Dashboard',       roles: null },
      { to: '/app/portfolio',      icon: 'bi-pie-chart',            label: 'Portfolio',        roles: [ROLES.SUPER_ADMIN, ROLES.OWNER, ROLES.PROJECT_MANAGER], tierFeature: 'portfolio_dashboard' },
      { to: '/app/projects',       icon: 'bi-kanban',               label: 'Projects',         roles: null },
    ],
  },
  {
    section: 'Administration',
    show: (hasRole) => hasRole(ROLES.SUPER_ADMIN, ROLES.OWNER, ROLES.PROJECT_MANAGER),
    items: [
      { to: '/app/users',          icon: 'bi-people',               label: 'Users',            roles: [ROLES.SUPER_ADMIN, ROLES.OWNER, ROLES.PROJECT_MANAGER] },
      { to: '/app/master-data',    icon: 'bi-database',             label: 'Master Data',      roles: [ROLES.SUPER_ADMIN, ROLES.OWNER, ROLES.PROJECT_MANAGER] },
      { to: '/app/vendors',        icon: 'bi-shop',                 label: 'Vendors',          roles: [ROLES.SUPER_ADMIN, ROLES.OWNER, ROLES.PROJECT_MANAGER], tierFeature: 'vendor_management' },
    ],
  },
  {
    section: 'System',
    show: (hasRole) => hasRole(ROLES.SUPER_ADMIN, ROLES.OWNER),
    items: [
      { to: '/app/alerts',         icon: 'bi-bell',                 label: 'Alerts',           roles: [ROLES.SUPER_ADMIN, ROLES.OWNER, ROLES.PROJECT_MANAGER], tierFeature: 'alert_rules' },
      { to: '/app/audit-logs',     icon: 'bi-journal-text',         label: 'Audit Logs',       roles: [ROLES.SUPER_ADMIN, ROLES.OWNER] },
      { to: '/app/notifications',  icon: 'bi-bell',                 label: 'Notifications',    roles: null },
      { to: '/app/platform-admin', icon: 'bi-shield-lock',          label: 'Platform Admin',   roles: [ROLES.SUPER_ADMIN] },
      { to: '/app/admin/payment-settings',     icon: 'bi-credit-card', label: 'Payment Settings',     roles: [ROLES.SUPER_ADMIN] },
      { to: '/app/admin/integration-settings', icon: 'bi-plug',        label: 'Integration Settings', roles: [ROLES.SUPER_ADMIN] },
    ],
  },
];

/* ─── Construction phase navigation ─────────────────────────────── */
const getConstructionNav = (projectId) => [
  {
    section: 'Daily Operations',
    items: [
      { to: `/app/projects/${projectId}/attendance`,     icon: 'bi-person-check',            label: 'Attendance' },
      { to: `/app/projects/${projectId}/work-progress`,  icon: 'bi-hammer',                  label: 'Work Progress' },
      { to: `/app/projects/${projectId}/materials`,       icon: 'bi-box-seam',                label: 'Materials' },
      { to: `/app/projects/${projectId}/expenses`,        icon: 'bi-wallet2',                 label: 'Expenses' },
      { to: `/app/projects/${projectId}/site-photos`,     icon: 'bi-camera',                  label: 'Site Photos' },
      { to: `/app/projects/${projectId}/day-close`,       icon: 'bi-moon-stars',              label: 'Day Close' },
    ],
  },
  {
    section: 'Planning',
    items: [
      { to: `/app/projects/${projectId}/milestones`,      icon: 'bi-flag',                    label: 'Milestones' },
      { to: `/app/projects/${projectId}/tasks`,            icon: 'bi-check2-square',           label: 'Tasks' },
      { to: `/app/projects/${projectId}/boq`,              icon: 'bi-calculator',              label: 'BOQ' },
      { to: `/app/projects/${projectId}/drawings`,         icon: 'bi-rulers',                  label: 'Drawings' },
      { to: `/app/projects/${projectId}/change-orders`,    icon: 'bi-pencil-square',           label: 'Change Orders' },
    ],
  },
  {
    section: 'Safety',
    items: [
      { to: `/app/projects/${projectId}/safety`,           icon: 'bi-shield-check',            label: 'Safety' },
      { to: `/app/projects/${projectId}/geo-punch`,        icon: 'bi-geo-alt',                 label: 'Geo Punch' },
    ],
  },
  {
    section: 'Insights & Billing',
    items: [
      { to: `/app/projects/${projectId}/timeline`,        icon: 'bi-bar-chart-steps',         label: 'Timeline' },
      { to: `/app/projects/${projectId}/reconciliation`,  icon: 'bi-clipboard2-data',         label: 'Reconciliation' },
      { to: `/app/projects/${projectId}/anomalies`,       icon: 'bi-robot',                   label: 'Anomalies' },
      { to: `/app/projects/${projectId}/invoices`,        icon: 'bi-receipt',                 label: 'Invoices' },
    ],
  },
];

/* ─── Community phase navigation ────────────────────────────────── */
const getCommunityNav = (projectId) => [
  {
    section: 'Management',
    items: [
      { to: `/app/projects/${projectId}/dashboard`,       icon: 'bi-speedometer2',            label: 'Dashboard' },
      { to: `/app/projects/${projectId}/units`,            icon: 'bi-building',                label: 'Units' },
      { to: `/app/projects/${projectId}/residents`,        icon: 'bi-people',                  label: 'Residents' },
    ],
  },
  {
    section: 'Operations',
    items: [
      { to: `/app/projects/${projectId}/billing`,          icon: 'bi-cash-stack',              label: 'Billing' },
      { to: `/app/projects/${projectId}/complaints`,       icon: 'bi-exclamation-triangle',    label: 'Complaints' },
      { to: `/app/projects/${projectId}/work-orders`,      icon: 'bi-wrench-adjustable',       label: 'Work Orders' },
      { to: `/app/projects/${projectId}/notices`,           icon: 'bi-megaphone',               label: 'Notices' },
    ],
  },
  {
    section: 'Security',
    items: [
      { to: `/app/projects/${projectId}/visitor-passes`,   icon: 'bi-person-badge',            label: 'Visitor Passes' },
      { to: `/app/projects/${projectId}/staff-attendance`,  icon: 'bi-clipboard-check',         label: 'Staff Attendance' },
    ],
  },
  {
    section: 'Community',
    items: [
      { to: `/app/projects/${projectId}/amenities`,    icon: 'bi-calendar2-event',   label: 'Amenity Booking' },
      { to: `/app/projects/${projectId}/polls`,        icon: 'bi-bar-chart',         label: 'Polls' },
      { to: `/app/projects/${projectId}/classifieds`,  icon: 'bi-tag',               label: 'Classifieds' },
    ],
  },
];

/* ─── Handover phase navigation ─────────────────────────────────── */
const getHandoverNav = (projectId) => [
  {
    section: 'Handover',
    items: [
      { to: `/app/projects/${projectId}/snags`,      icon: 'bi-clipboard2-x', label: 'Snags' },
      { to: `/app/projects/${projectId}/handovers`,  icon: 'bi-key',           label: 'Unit Handovers' },
    ],
  },
];

/* ─── Shared navigation (always shown inside a project) ─────────── */
const getSharedNav = (projectId) => [
  {
    section: 'Shared',
    items: [
      { to: `/app/projects/${projectId}/documents`,  icon: 'bi-folder2-open',           label: 'Documents' },
      { to: `/app/projects/${projectId}/chat`,       icon: 'bi-chat-dots',              label: 'Chat' },
      { to: `/app/projects/${projectId}/reports`,    icon: 'bi-file-earmark-bar-graph', label: 'Reports' },
      { to: `/app/projects/${projectId}/settings`,   icon: 'bi-gear',                   label: 'Settings' },
    ],
  },
];

/* ═══════════════════════════════════════════════════════════════════
   Sidebar Component
   ═══════════════════════════════════════════════════════════════════ */
const Sidebar = ({ collapsed, mobileOpen, onCloseMobile }) => {
  const { user, hasRole } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const { project, loadProject } = useProject();

  // The sidebar renders at the /app layout level, so useParams() cannot see the
  // :projectId of a descendant route — it always came back empty, which meant the
  // project navigation never appeared. Read it from the path instead.
  const projectId = location.pathname.match(/\/projects\/([^/]+)/)?.[1];
  const isInsideProject = Boolean(projectId);

  // Nothing else pulls the project into context, so the phase stayed null and the
  // nav could never switch away from construction. Load it here.
  useEffect(() => {
    if (projectId) loadProject(projectId);
  }, [projectId, loadProject]);

  // Build the navigation items based on context
  const navSections = useMemo(() => {
    if (!isInsideProject) {
      return getPortfolioNav(t);
    }

    const phase = project?.phase || 'construction';
    let phaseNav = [];

    if (phase === 'community') {
      phaseNav = getCommunityNav(projectId);
    } else if (phase === 'handover') {
      // Handover keeps the snag list from construction, and adds possession.
      phaseNav = [...getHandoverNav(projectId), ...getConstructionNav(projectId)];
    } else {
      // Construction, and anything unrecognised. Snagging starts here, so the
      // snag list is reachable before the site formally moves to handover.
      phaseNav = [
        ...getConstructionNav(projectId),
        {
          section: 'Handover',
          items: [
            { to: `/app/projects/${projectId}/snags`, icon: 'bi-clipboard2-x', label: 'Snags' },
          ],
        },
      ];
    }

    return [...phaseNav, ...getSharedNav(projectId)];
  }, [isInsideProject, project?.phase, projectId, t]);

  // Visibility check for role-based items
  const isVisible = (item) => {
    if (!item.roles) return true;
    return item.roles.includes(user?.role);
  };

  // Phase badge info
  const phaseBadge = project ? PHASE_BADGE[project.phase] || PHASE_BADGE.construction : null;

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={`sidebar-overlay ${mobileOpen ? 'show' : ''}`}
        onClick={onCloseMobile}
      />

      <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        {/* Brand */}
        <NavLink to="/app/dashboard" className="sidebar-brand" onClick={onCloseMobile}>
          <img
            src="/images/logo-projectrax.png"
            alt="ProjectRax"
            style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
          />
          <span className="sidebar-brand-text">
            Project<span style={{ color: '#1E6FBA' }}>Rax</span>
          </span>
        </NavLink>

        {/* Project header (shown when inside a project) */}
        {isInsideProject && project && !collapsed && (
          <div className="sidebar-project-header">
            <Link
              to="/app/projects"
              className="sidebar-back-link"
              onClick={onCloseMobile}
            >
              <i className="bi bi-arrow-left"></i>
              <span>Back to Projects</span>
            </Link>
            <div className="sidebar-project-name" title={project.name}>
              {project.name}
            </div>
            {phaseBadge && (
              <span className={`badge ${phaseBadge.bg} ${phaseBadge.text} sidebar-phase-badge`}>
                {phaseBadge.label}
              </span>
            )}
          </div>
        )}

        {/* Collapsed project indicator */}
        {isInsideProject && project && collapsed && (
          <div className="text-center py-2">
            <Link
              to="/app/projects"
              className="d-inline-block text-white-50"
              title="Back to Projects"
              onClick={onCloseMobile}
            >
              <i className="bi bi-arrow-left-circle" style={{ fontSize: '1.25rem' }}></i>
            </Link>
          </div>
        )}

        {/* Navigation */}
        <nav className="sidebar-nav">
          {navSections.map((section) => {
            // Check section-level visibility
            if (section.show && !section.show(hasRole)) return null;

            const visibleItems = section.items.filter(isVisible);
            if (visibleItems.length === 0) return null;

            return (
              <div key={section.section}>
                <div className="sidebar-section-label">{section.section}</div>
                {visibleItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to.endsWith('/dashboard') || item.to === '/app/projects'}
                    className={({ isActive }) => `sidebar-nav-link ${isActive ? 'active' : ''}`}
                    onClick={onCloseMobile}
                  >
                    <i className={`bi ${item.icon}`}></i>
                    <span className="sidebar-nav-text">
                      {item.label}
                      {item.tierFeature && <TierBadge feature={item.tierFeature} />}
                    </span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.75rem', opacity: 0.5 }}>
            <i className="bi bi-shield-check"></i>
            <span className="sidebar-nav-text">v1.0.0</span>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
